import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {z} from 'zod';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {NO_NODE_ID} from 'common/src/components/CLCardsMap/CLCardsMap.config';
import {WeightedNodeOutputData} from 'common/src/modules/decisionTree/decisionTree.types';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {WeightedCardScratchpadState} from '../../scratchpad/weightedCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';

const addWeightedOutputInputSchema = z.object({
  name: z.string().describe('The name of the output'),
  weight: z
    .number()
    .int({message: 'Weight must be a whole number'})
    .positive({message: 'Weight must be positive'})
    .min(1, {message: 'Weight must be at least 1'})
    .describe('The weight of the output (positive integer, minimum 1)'),
});

export default function addWeightedOutputTool() {
  async function execute(
    params: ToolExecuteParams<typeof addWeightedOutputInputSchema, WeightedCardScratchpadState>,
  ) {
    const {name, weight} = params.input;
    const {agentScratchpad} = params.toolUtils;
    const validatedScratchpad = validateAgentScratchpad<WeightedCardScratchpadState>(
      agentScratchpad,
      NodeType.WEIGHTED,
    );

    const nextId =
      validatedScratchpad.card.outputs.length > 0
        ? Math.max(...validatedScratchpad.card.outputs.map((o) => o.id), 0) + 1
        : 1;

    const newOutput: WeightedNodeOutputData = {
      id: nextId,
      nodeId: NO_NODE_ID,
      name: name ?? `Output ${nextId}`,
      weight: weight,
    };

    validatedScratchpad.card.outputs.push(newOutput);

    return toolResult(`Weighted output "${name}" with weight ${weight} added successfully`);
  }

  return tool<typeof addWeightedOutputInputSchema, WeightedCardScratchpadState>({
    name: V2CardAgentToolName.AddWeightedOutput,
    displayName: 'Adding weighted output',
    description: 'Add a new output with a specified weight (positive integer, minimum 1).',
    inputSchema: addWeightedOutputInputSchema,
    execute,
  });
}
